import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Play, Wifi, Copy, Trophy, CheckCircle2, XCircle, Zap, ArrowRight, Loader2, QrCode, Link2, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
const SITE_URL = 'https://pantass.com';

export default function ClassroomPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const classFromNav = location.state || {};
  const classStudents = classFromNav.students || [];
  const classNameFromNav = classFromNav.className || '';
  const [mode, setMode] = useState(classStudents.length > 0 ? 'create' : 'menu'); // auto-go to create if from class
  const [studentName, setStudentName] = useState(''); // for student joining with class roster
  const [roomCode, setRoomCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState(null);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [totalQ, setTotalQ] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [answered, setAnswered] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [myScore, setMyScore] = useState(0);
  const [toast, setToast] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genSubject, setGenSubject] = useState('matematik');
  const [genDifficulty, setGenDifficulty] = useState('easy');
  const [genCount, setGenCount] = useState(10);
  const [genTopic, setGenTopic] = useState('');
  const [roomRoster, setRoomRoster] = useState([]);  // student names from class roster
  const [joinedNames, setJoinedNames] = useState([]); // names already taken
  const wsRef = useRef(null);
  const isEN = language === 'en';
  const plan = user?.plan || 'free';
  const isSchool = plan === 'school';
  const isTeacher = user && (user.plan === 'school');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  const connectWS = (code, role, customName) => {
    const ws = new WebSocket(`${WS_URL}/api/classroom/ws/${code}`);
    wsRef.current = ws;
    ws.onopen = () => {
      const name = customName || studentName || user?.name || 'Student';
      ws.send(JSON.stringify({ role, email: user?.email || `guest-${Date.now()}`, name }));
    };
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      handleWSMessage(msg, role);
    };
    ws.onclose = () => { console.log('WS closed'); };
    ws.onerror = (e) => { console.error('WS error', e); };
  };

  const handleWSMessage = (msg, role) => {
    switch(msg.type) {
      case 'connected':
        setSession(msg.session);
        break;
      case 'student_joined':
        setSession(prev => prev ? {...prev, student_count: msg.student_count, students: msg.students} : prev);
        break;
      case 'student_left':
        setSession(prev => prev ? {...prev, student_count: msg.student_count, students: msg.students} : prev);
        break;
      case 'game_started':
        setMode('playing');
        setQuestion(msg.question);
        setQuestionIdx(msg.question_index);
        setTotalQ(msg.total);
        setAnswered(false);
        setLastResult(null);
        break;
      case 'new_question':
        setQuestion(msg.question);
        setQuestionIdx(msg.question_index);
        setTotalQ(msg.total);
        setAnswered(false);
        setLastResult(null);
        if (msg.leaderboard) setLeaderboard(msg.leaderboard);
        break;
      case 'answer_result':
        setAnswered(true);
        setLastResult(msg);
        setMyScore(msg.score);
        break;
      case 'student_answered':
        if (msg.leaderboard) setLeaderboard(msg.leaderboard);
        break;
      case 'game_finished':
        setMode('finished');
        setLeaderboard(msg.leaderboard);
        break;
      case 'error':
        showToast(msg.message, 'error');
        break;
      default:
        break;
    }
  };

  const createRoom = async () => {
    setGenerating(true);
    try {
      // Generate AI questions
      const qRes = await fetch(`${BACKEND_URL}/api/questions/generate`, {
        method: 'POST', headers,
        body: JSON.stringify({ subject: genSubject, topic: genTopic, difficulty: genDifficulty, count: genCount, language: 'both' })
      });
      let questions = [];
      if (qRes.ok) {
        const qData = await qRes.json();
        questions = qData.questions || [];
      }
      if (questions.length === 0) {
        showToast(isEN ? 'Failed to generate questions' : 'Gagal generate soalan', 'error');
        setGenerating(false);
        return;
      }
      // Create room
      const res = await fetch(`${BACKEND_URL}/api/classroom/create`, {
        method: 'POST', headers,
        body: JSON.stringify({ subject: genSubject, difficulty: genDifficulty, questions, roster: classStudents })
      });
      if (res.ok) {
        const data = await res.json();
        setRoomCode(data.room_code);
        setSession(data.session);
        setMode('waiting');
        connectWS(data.room_code, 'teacher');
      } else {
        const e = await res.json();
        showToast(e.detail || 'Error', 'error');
      }
    } catch(e) { showToast('Error creating room', 'error'); }
    setGenerating(false);
  };

  const joinRoom = () => {
    if (!joinCode.trim()) return;
    const code = joinCode.trim().toUpperCase();
    setJoinCode(code);
    // Fetch room info to get roster
    fetchRoomInfo(code);
    setMode('enter-name');
  };

  const fetchRoomInfo = async (code) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/classroom/join/${code}`);
      if (res.ok) {
        const data = await res.json();
        setRoomRoster(data.roster || []);
        setJoinedNames(data.joined_names || []);
      }
    } catch (e) { console.error(e); }
  };

  const confirmJoinWithName = () => {
    if (!studentName.trim()) return;
    const code = joinCode.trim().toUpperCase();
    setRoomCode(code);
    setMode('waiting');
    connectWS(code, 'student', studentName.trim());
  };

  const startGame = () => {
    if (wsRef.current) wsRef.current.send(JSON.stringify({ type: 'start_game' }));
  };

  const nextQuestion = () => {
    if (wsRef.current) wsRef.current.send(JSON.stringify({ type: 'next_question' }));
  };

  const endGame = () => {
    if (wsRef.current) wsRef.current.send(JSON.stringify({ type: 'end_game' }));
  };

  const submitAnswer = (answerIdx) => {
    if (answered || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: 'answer', answer: answerIdx }));
  };

  const copyCode = () => {
    navigator.clipboard.writeText(roomCode);
    showToast(isEN ? 'Copied!' : 'Disalin!');
  };

  useEffect(() => { return () => { if (wsRef.current) wsRef.current.close(); }; }, []);

  // Teacher: check for active sessions and auto-reconnect
  useEffect(() => {
    if (!isTeacher || !token || mode !== 'menu') return;
    const checkActive = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/classroom/active`, { headers });
        if (res.ok) {
          const sessions = await res.json();
          if (sessions.length > 0) {
            const active = sessions[0];
            setRoomCode(active.room_code);
            setSession(active);
            setMode('waiting');
            connectWS(active.room_code, 'teacher');
          }
        }
      } catch (e) { console.error(e); }
    };
    checkActive();
  }, [token, isTeacher]);

  // Auto-join from URL param ?code=XXXXXX — show name entry first
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl && codeFromUrl.trim().length >= 4 && mode === 'menu') {
      const code = codeFromUrl.trim().toUpperCase();
      setJoinCode(code);
      fetchRoomInfo(code);
      setMode('enter-name');
    }
  }, [searchParams]);

  const getJoinLink = (code) => `${SITE_URL}/classroom?code=${code}`;

  const copyLink = () => {
    navigator.clipboard.writeText(getJoinLink(roomCode));
    showToast(isEN ? 'Link copied!' : 'Link disalin!');
  };

  const shareWhatsApp = () => {
    const msg = isEN
      ? `Join my PANTASS Classroom! Code: ${roomCode}\nClick to join: ${getJoinLink(roomCode)}`
      : `Join Bilik Darjah PANTASS saya! Kod: ${roomCode}\nKlik untuk masuk: ${getJoinLink(roomCode)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">{isEN ? 'Classroom' : 'Bilik Darjah'}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        <AnimatePresence>{toast && <motion.div className={`shop-toast shop-toast-${toast.type}`} initial={{opacity:0,y:-20}} animate={{opacity:1,y:0}} exit={{opacity:0}}>{toast.msg}</motion.div>}</AnimatePresence>

        {/* Menu */}
        {mode === 'menu' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="cr-menu" data-testid="classroom-menu">
            <div className="cr-menu-hero">
              <Users size={40} color="#009688" />
              <h2>{isEN ? 'Classroom Mode' : 'Mod Bilik Darjah'}</h2>
              <p>{isEN ? 'Play with your whole class in real-time! Up to 50 students.' : 'Main dengan seluruh kelas secara langsung! Sehingga 50 murid.'}</p>
              {!isSchool && <div className="cr-plan-badge">{isEN ? 'School Plan Only' : 'Pelan Sekolah Sahaja'}</div>}
            </div>
            {isSchool && (
              <button className="cr-create-btn" onClick={() => setMode('create')} data-testid="classroom-create-btn">
                <Wifi size={20} /> {isEN ? 'Create Room' : 'Buat Bilik'}
              </button>
            )}
            <div className="cr-join-section">
              <h3>{isEN ? 'Join a Room' : 'Masuk Bilik'}</h3>
              <div className="cr-join-row">
                <input className="cr-join-input" value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())}
                  placeholder={isEN ? 'Enter room code' : 'Masukkan kod bilik'} maxLength={6} data-testid="classroom-join-input" />
                <button className="cr-join-btn" onClick={joinRoom} disabled={!joinCode.trim()} data-testid="classroom-join-btn">
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
            <button className="setup-back-btn" onClick={() => navigate('/')} style={{marginTop:'20px'}}>&larr; {t('backToHome', language)}</button>
          </motion.div>
        )}

        {/* Enter Name (Student - no account needed) */}
        {mode === 'enter-name' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="cr-enter-name" data-testid="classroom-enter-name">
            <div className="cr-name-hero">
              <Users size={36} color="#009688" />
              <h2>{isEN ? 'Join Classroom' : 'Masuk Bilik Darjah'}</h2>
              <p>{isEN ? `Room Code: ${joinCode}` : `Kod Bilik: ${joinCode}`}</p>
            </div>
            <div className="cr-name-form">
              {roomRoster.length > 0 ? (
                <>
                  <label>{isEN ? 'Select your name:' : 'Pilih nama kamu:'}</label>
                  <select
                    className="cr-name-input"
                    value={studentName}
                    onChange={e => setStudentName(e.target.value)}
                    data-testid="classroom-student-name-select"
                  >
                    <option value="">{isEN ? '-- Pick your name --' : '-- Pilih nama kamu --'}</option>
                    {roomRoster.filter(n => !joinedNames.includes(n)).map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <p className="cr-name-hint">{isEN ? 'Pick your name from the class list' : 'Pilih nama dari senarai kelas'}</p>
                </>
              ) : (
                <>
                  <label>{isEN ? 'What is your name?' : 'Siapa nama kamu?'}</label>
                  <input
                    className="cr-name-input"
                    value={studentName}
                    onChange={e => setStudentName(e.target.value)}
                    placeholder={isEN ? 'Enter your name' : 'Masukkan nama kamu'}
                    maxLength={25}
                    autoFocus
                    data-testid="classroom-student-name"
                    onKeyDown={e => e.key === 'Enter' && confirmJoinWithName()}
                  />
                </>
              )}
              <button className="cr-create-btn" onClick={confirmJoinWithName} disabled={!studentName.trim()} data-testid="classroom-join-confirm">
                <ArrowRight size={20} /> {isEN ? 'Join Game!' : 'Masuk Game!'}
              </button>
            </div>
            <button className="setup-back-btn" onClick={() => setMode('menu')} style={{marginTop:'12px'}}>&larr; {isEN ? 'Back' : 'Kembali'}</button>
          </motion.div>
        )}

        {/* Create Room (Teacher) */}
        {mode === 'create' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="cr-create" data-testid="classroom-create">
            <h2>{isEN ? 'Create Classroom Session' : 'Buat Sesi Bilik Darjah'}</h2>
            <label>{isEN ? 'Subject' : 'Subjek'}</label>
            <select className="rw-input" value={genSubject} onChange={e => setGenSubject(e.target.value)} data-testid="cr-subject">
              <option value="matematik">Matematik</option>
              <option value="sains">Sains</option>
              <option value="bahasa-melayu">Bahasa Melayu</option>
              <option value="english">English</option>
              <option value="sejarah">Sejarah</option>
              <option value="geografi">Geografi</option>
            </select>
            <label>{isEN ? 'Topic (optional)' : 'Topik (pilihan)'}</label>
            <input className="rw-input" value={genTopic} onChange={e => setGenTopic(e.target.value)}
              placeholder={isEN ? 'e.g. Fractions, Photosynthesis' : 'cth. Pecahan, Fotosintesis'} data-testid="cr-topic" />
            <label>{isEN ? 'Difficulty' : 'Tahap'}</label>
            <select className="rw-input" value={genDifficulty} onChange={e => setGenDifficulty(e.target.value)} data-testid="cr-difficulty">
              <option value="easy">{isEN ? 'Easy (Std 1-3)' : 'Mudah (Drjh 1-3)'}</option>
              <option value="medium">{isEN ? 'Medium (Std 4-6)' : 'Sederhana (Drjh 4-6)'}</option>
              <option value="hard">{isEN ? 'Hard (Form 1-3)' : 'Sukar (Ting 1-3)'}</option>
            </select>
            <label>{isEN ? 'Number of Questions' : 'Bilangan Soalan'}</label>
            <select className="rw-input" value={genCount} onChange={e => setGenCount(Number(e.target.value))} data-testid="cr-count">
              {[5,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button className="cr-create-btn" onClick={createRoom} disabled={generating} data-testid="cr-generate-btn" style={{marginTop:'16px'}}>
              {generating ? <><Loader2 size={18} className="spin" /> {isEN ? 'Generating...' : 'Menjana...'}</> : <><Zap size={18} /> {isEN ? 'Generate & Create Room' : 'Jana & Buat Bilik'}</>}
            </button>
            <button className="setup-back-btn" onClick={() => setMode('menu')} style={{marginTop:'12px'}}>&larr; {isEN ? 'Back' : 'Kembali'}</button>
          </motion.div>
        )}

        {/* Waiting Room */}
        {mode === 'waiting' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="cr-waiting" data-testid="classroom-waiting">
            <div className="cr-code-display">
              <div className="cr-code-label">{isEN ? 'Room Code' : 'Kod Bilik'}</div>
              <div className="cr-code-value" data-testid="room-code">{roomCode}</div>
              <button className="cr-code-copy" onClick={copyCode}><Copy size={16} /> {isEN ? 'Copy Code' : 'Salin Kod'}</button>
            </div>

            {/* Share Options */}
            <div className="cr-share-section" data-testid="classroom-share">
              <h4 className="cr-share-title">{isEN ? 'Share with Students' : 'Kongsi kepada Murid'}</h4>

              {/* Direct Link */}
              <div className="cr-share-link-box">
                <input readOnly value={getJoinLink(roomCode)} className="cr-share-link-input" data-testid="classroom-join-link" />
                <button className="cr-share-link-copy" onClick={copyLink} data-testid="classroom-copy-link">
                  <Link2 size={14} /> {isEN ? 'Copy' : 'Salin'}
                </button>
              </div>

              {/* Action Buttons */}
              <div className="cr-share-btns">
                <button className="cr-share-btn cr-share-qr" onClick={() => setShowQR(!showQR)} data-testid="classroom-show-qr">
                  <QrCode size={18} /> {isEN ? 'QR Code' : 'Kod QR'}
                </button>
                <button className="cr-share-btn cr-share-wa" onClick={shareWhatsApp} data-testid="classroom-share-wa">
                  <Share2 size={18} /> WhatsApp
                </button>
              </div>

              {/* QR Code */}
              <AnimatePresence>
                {showQR && (
                  <motion.div className="cr-qr-container" data-testid="classroom-qr-code"
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                    <div className="cr-qr-card">
                      <QRCodeSVG value={getJoinLink(roomCode)} size={200} level="M"
                        bgColor="#ffffff" fgColor="#009688"
                        imageSettings={{ src: '', height: 0, width: 0, excavate: false }} />
                      <p className="cr-qr-hint">{isEN ? 'Students scan this QR code to join instantly' : 'Murid scan kod QR ini untuk masuk terus'}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="cr-students-count">
              <Users size={20} />
              <span>{session?.student_count || 0} {isEN ? 'students joined' : 'murid masuk'}</span>
            </div>
            {session?.students && session.students.length > 0 && (
              <div className="cr-student-list">
                {session.students.map((s,i) => <div key={i} className="cr-student-chip">{s.name}</div>)}
              </div>
            )}

            {/* Teacher Controls — always show if user is teacher (created the room) */}
            {isTeacher && (
              <div className="cr-teacher-waiting-controls" data-testid="cr-teacher-controls">
                <button className="cr-start-btn" onClick={startGame} disabled={(session?.student_count || 0) < 1} data-testid="cr-start-game">
                  <Play size={20} /> {isEN ? 'Start Game!' : 'Mula Permainan!'}
                </button>
                <button className="cr-end-session-btn" onClick={() => { endGame(); setMode('menu'); if(wsRef.current) wsRef.current.close(); }} data-testid="cr-end-session">
                  <XCircle size={16} /> {isEN ? 'End Session' : 'Tamat Sesi'}
                </button>
              </div>
            )}

            {/* Student waiting message */}
            {!isTeacher && (
              <div className="cr-student-waiting" data-testid="cr-student-waiting">
                <Loader2 size={24} className="spin" />
                <p>{isEN ? 'Waiting for teacher to start the game...' : 'Menunggu cikgu mulakan permainan...'}</p>
              </div>
            )}

            <p className="cr-waiting-hint">{isTeacher ? (isEN ? 'Share the room code, link, or QR code with your students.' : 'Kongsi kod bilik, link, atau kod QR kepada murid.') : (isEN ? 'Get ready! The game will start soon.' : 'Bersedia! Permainan akan bermula tidak lama lagi.')}</p>
          </motion.div>
        )}

        {/* Playing */}
        {mode === 'playing' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="cr-playing" data-testid="classroom-playing">
            <div className="cr-q-header">
              <span className="cr-q-counter">{isEN ? 'Question' : 'Soalan'} {questionIdx + 1}/{totalQ}</span>
              {session?.teacher_name === user?.name && <span className="cr-q-role-badge">Teacher View</span>}
            </div>
            {question && (
              <div className="cr-question-card">
                <h3 className="cr-q-text">{isEN ? question.q_en : (question.q_bm || question.q_en)}</h3>
                <div className="cr-options">
                  {(question.options || []).map((opt, i) => (
                    <button key={i} className={`cr-option ${answered ? (i === question.answer ? 'cr-option-correct' : lastResult && !lastResult.correct && i === lastResult?.selected ? 'cr-option-wrong' : '') : ''} ${answered ? 'cr-option-disabled' : ''}`}
                      onClick={() => submitAnswer(i)} disabled={answered || session?.teacher_name === user?.name}
                      data-testid={`cr-option-${i}`}>
                      <span className="cr-option-letter">{['A','B','C','D'][i]}</span>
                      <span>{opt}</span>
                    </button>
                  ))}
                </div>
                {answered && lastResult && (
                  <div className={`cr-result-badge ${lastResult.correct ? 'cr-result-correct' : 'cr-result-wrong'}`}>
                    {lastResult.correct ? <><CheckCircle2 size={18} /> {isEN ? 'Correct! +10' : 'Betul! +10'}</> : <><XCircle size={18} /> {isEN ? 'Wrong!' : 'Salah!'}</>}
                  </div>
                )}
              </div>
            )}
            {/* Teacher: Next/End buttons */}
            {session?.teacher_name === user?.name && (
              <div className="cr-teacher-controls">
                <button className="cr-next-btn" onClick={nextQuestion} data-testid="cr-next-question">
                  {questionIdx + 1 >= totalQ ? (isEN ? 'Finish' : 'Tamat') : (isEN ? 'Next Question' : 'Soalan Seterusnya')} <ArrowRight size={16} />
                </button>
                <button className="cr-end-btn" onClick={endGame}>{isEN ? 'End Game' : 'Tamat Permainan'}</button>
              </div>
            )}
            {/* Live Leaderboard */}
            {leaderboard.length > 0 && (
              <div className="cr-live-lb">
                <h4><Trophy size={16} color="#f59e0b" /> {isEN ? 'Live Leaderboard' : 'Papan Markah Langsung'}</h4>
                {leaderboard.slice(0,10).map((s,i) => (
                  <div key={i} className={`cr-lb-row ${i < 3 ? 'cr-lb-top' : ''}`}>
                    <span className="cr-lb-rank">{i === 0 ? '\uD83E\uDD47' : i === 1 ? '\uD83E\uDD48' : i === 2 ? '\uD83E\uDD49' : i+1}</span>
                    <span className="cr-lb-name">{s.name}</span>
                    <span className="cr-lb-score">{s.score}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Student score */}
            {session?.teacher_name !== user?.name && (
              <div className="cr-my-score">{isEN ? 'Your Score' : 'Skor Anda'}: <strong>{myScore}</strong></div>
            )}
          </motion.div>
        )}

        {/* Finished */}
        {mode === 'finished' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="cr-finished" data-testid="classroom-finished">
            <div className="cr-finish-hero">
              <Trophy size={48} color="#f59e0b" />
              <h2>{isEN ? 'Game Complete!' : 'Permainan Selesai!'}</h2>
            </div>
            <div className="cr-final-lb">
              <h3>{isEN ? 'Final Leaderboard' : 'Papan Markah Akhir'}</h3>
              {leaderboard.map((s,i) => (
                <div key={i} className={`cr-lb-row ${i < 3 ? 'cr-lb-top' : ''} ${s.name === user?.name ? 'cr-lb-me' : ''}`}>
                  <span className="cr-lb-rank">{i === 0 ? '\uD83E\uDD47' : i === 1 ? '\uD83E\uDD48' : i === 2 ? '\uD83E\uDD49' : i+1}</span>
                  <span className="cr-lb-name">{s.name}</span>
                  <span className="cr-lb-score">{s.score} pts</span>
                  <span className="cr-lb-detail">{s.correct}/{s.correct + s.wrong} {isEN ? 'correct' : 'betul'}</span>
                </div>
              ))}
            </div>
            <button className="cr-create-btn" onClick={() => { setMode('menu'); if(wsRef.current) wsRef.current.close(); }} style={{marginTop:'20px'}}>
              {isEN ? 'Back to Menu' : 'Kembali ke Menu'}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
